import { axiosWithToken } from "@/utils/axios";

import parseHttpError from "@/utils/parseHttpError";
import {
  PropertyEdit,
  BulkOperationRequest,
  BulkOperationProgress,
  AuditLog,
  AuditLogFilters,
  PropertyManagementResponse,
  PropertyEditFormData,
} from "@/types/admin";

class AdminPropertyManagementService {
  /**
   * Edit a property with admin privileges
   */
  async editProperty(
    data: PropertyEditFormData,
  ): Promise<PropertyManagementResponse<PropertyEdit>> {
    try {
      const payload: Record<string, any> = {
        address: data.address,
        description: data.description,
        price: data.price,
        totalPackage: data.total_package,
        agencyFees: data.agency_fees,
        legalFees: data.legal_fees,
        cautionFee: data.caution_fee,
        bedroomCount: data.bedroom_count,
        numberOfPlots: data.number_of_plots,
        numberOfDays: data.number_of_days,
        propertyType: data.property_type,
        listingType: data.listing_type,
        status: data.status,
        furnished: data.furnished,
        city: data.city,
        state: data.state,
        additionalInfo: data.additional_info,
        image_url: data.image_url,
        property_category: data.property_category,
        amenities: data.amenities,
        serviced: data.serviced,
        shared: data.shared,
        isNegotiable: data.is_negotiable,
        propertySizeSqm: (data as any).property_size_sqm,
        numberOfFloors: (data as any).number_of_floors,
        parkingSpaces: (data as any).parking_spaces,
        newlyBuilt: (data as any).newly_built,
        floorAreaSqm: (data as any).floor_area_sqm,
        floorNumber: (data as any).floor_number,
        numberOfPrivateOffices: (data as any).number_of_private_offices,
        meetingRooms: (data as any).meeting_rooms,
        conferenceRooms: (data as any).conference_rooms,
        receptionArea: (data as any).reception_area,
        openWorkspaceCapacity: (data as any).open_workspace_capacity,
        parkingCapacity: (data as any).parking_capacity,
        dedicatedDesks: (data as any).dedicated_desks,
        privateOffices: (data as any).private_offices,
        capacity: (data as any).capacity,
        internetSpeed: (data as any).internet_speed,
        printingAvailable: (data as any).printing_available,
        shopSizeSqm: (data as any).shop_size_sqm,
        storageRoom: (data as any).storage_room,
        electricity: (data as any).electricity,
        waterSupply: (data as any).water_supply,
        displayAreaSqm: (data as any).display_area_sqm,
        officeArea: (data as any).office_area,
        storageArea: (data as any).storage_area,
        ceilingHeight: (data as any).ceiling_height,
        glassFrontageLength: (data as any).glass_frontage_length,
        diningCapacity: (data as any).dining_capacity,
        kitchenSize: (data as any).kitchen_size,
        indoorSeating: (data as any).indoor_seating,
        outdoorSeating: (data as any).outdoor_seating,
        numberOfRestrooms: (data as any).number_of_restrooms,
        privateDiningRooms: (data as any).private_dining_rooms,
        barArea: (data as any).bar_area,
        numberOfRooms: (data as any).number_of_rooms,
        roomTypes: (data as any).room_types,
        numberOfSuites: (data as any).number_of_suites,
        conferenceRoomsCount: (data as any).conference_rooms_count,
        banquetHall: (data as any).banquet_hall,
        receptionAreaField: (data as any).reception_area,
        hallCapacity: (data as any).hall_capacity,
        numberOfEventHalls: (data as any).number_of_event_halls,
        vipLounge: (data as any).vip_lounge,
        bridalRoom: (data as any).bridal_room,
        waitingLounge: (data as any).waiting_lounge,
        dressingRoom: (data as any).dressing_room,
        numberOfToilets: (data as any).number_of_toilets,
        stage: (data as any).stage,
        ledScreen: (data as any).led_screen,
        soundSystem: (data as any).sound_system,
        lighting: (data as any).lighting,
        wheelchairAccess: (data as any).wheelchair_access,
        warehouseSize: (data as any).warehouse_size,
        loadingBays: (data as any).loading_bays,
        dockLevelers: (data as any).dock_levelers,
        truckAccess: (data as any).truck_access,
        containerAccess: (data as any).container_access,
        officeAttached: (data as any).office_attached,
        security: (data as any).security,
        powerCapacity: (data as any).power_capacity,
        coldStorage: (data as any).cold_storage,
        fireSafetySystem: (data as any).fire_safety_system,
        factoryFloorArea: (data as any).factory_floor_area,
        warehouseArea: (data as any).warehouse_area,
        numberOfProductionFloors: (data as any).number_of_production_floors,
        floorLoadCapacity: (data as any).floor_load_capacity,
        dockDoors: (data as any).dock_doors,
        transformerCapacity: (data as any).transformer_capacity,
        generatorCapacity: (data as any).generator_capacity,
        borehole: (data as any).borehole,
        gasSupply: (data as any).gas_supply,
        wasteTreatmentFacility: (data as any).waste_treatment_facility,
        workshopArea: (data as any).workshop_area,
        yardSpace: (data as any).yard_space,
        floorStrength: (data as any).floor_strength,
        threePhasePower: (data as any).three_phase_power,
        compressedAirLines: (data as any).compressed_air_lines,
        coldStorageCapacity: (data as any).cold_storage_capacity,
        numberOfColdRooms: (data as any).number_of_cold_rooms,
        temperatureRange: (data as any).temperature_range,
        freezerCapacity: (data as any).freezer_capacity,
        chillerCapacity: (data as any).chiller_capacity,
        yardSize: (data as any).yard_size,
        numberOfDockDoors: (data as any).number_of_dock_doors,
        truckParkingCapacity: (data as any).truck_parking_capacity,
        trailerParkingCapacity: (data as any).trailer_parking_capacity,
        fleetParking: (data as any).fleet_parking,
        containerHoldingArea: (data as any).container_holding_area,
        serviceCharge: (data as any).service_charge,
        saleDocumentDetails: (data as any).sale_document_details,
        documentation: (data as any).documentation,
      };

      // Remove undefined/null values so backend only updates provided fields
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === null)
          delete payload[key];
      });

      const response = await axiosWithToken().patch(
        `/admin/properties/${data.id}`,
        payload,
      );
      return {
        success: true,
        data: response.data,
        message: "Property updated successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        error: parseHttpError(error).message,
      };
    }
  }

  /**
   * Perform bulk operations on multiple properties
   */
  async executeBulkOperation(
    request: BulkOperationRequest,
  ): Promise<PropertyManagementResponse<{ operationId: string }>> {
    try {
      const response = await axiosWithToken().post(
        "/admin/properties/bulk",
        request,
      );
      return {
        success: true,
        data: response.data,
        message: "Bulk operation initiated successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        error: parseHttpError(error).message,
      };
    }
  }

  /**
   * Get the progress of a bulk operation
   */
  async getBulkOperationProgress(
    operationId: string,
  ): Promise<PropertyManagementResponse<BulkOperationProgress>> {
    try {
      const response = await axiosWithToken().get(
        `/admin/properties/bulk/${operationId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: parseHttpError(error).message,
      };
    }
  }

  /**
   * Get audit logs for properties
   */
  async getAuditLogs(
    filters?: AuditLogFilters,
  ): Promise<PropertyManagementResponse<AuditLog[]>> {
    try {
      const response = await axiosWithToken().get(
        "/admin/properties/audit-logs",
        {
          params: filters,
        },
      );
      const payload = response.data?.data ?? response.data;
      return {
        success: true,
        data: Array.isArray(payload) ? payload : [],
      };
    } catch (error: any) {
      return {
        success: false,
        error: parseHttpError(error).message,
      };
    }
  }

  /**
   * Get audit logs for a specific property
   */
  async getPropertyAuditLogs(
    propertyId: string,
  ): Promise<PropertyManagementResponse<AuditLog[]>> {
    try {
      const response = await axiosWithToken().get(
        `/admin/properties/${propertyId}/audit-logs`,
      );
      const payload = response.data?.data ?? response.data;
      return {
        success: true,
        data: Array.isArray(payload) ? payload : [],
      };
    } catch (error: any) {
      return {
        success: false,
        error: parseHttpError(error).message,
      };
    }
  }

  /**
   * Update property status (publish/unpublish)
   */
  async updatePropertyStatus(
    propertyId: string,
    status: string,
  ): Promise<PropertyManagementResponse> {
    try {
      const response = await axiosWithToken().patch(
        `/admin/properties/${propertyId}/status`,
        { status },
      );
      return {
        success: true,
        data: response.data,
        message: "Property status updated successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        error: parseHttpError(error).message,
      };
    }
  }

  /**
   * Get all properties with admin privileges (no ownership restriction)
   */
  async getAllProperties(params?: any): Promise<PropertyManagementResponse> {
    try {
      const response = await axiosWithToken().get("/admin/properties", {
        params,
      });
      return {
        success: true,
        data: {
          properties: response.data.data || [],
          total: response.data.metadata?.totalCount || 0,
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: parseHttpError(error).message,
      };
    }
  }

  /**
   * Delete a property (admin override)
   */
  async deleteProperty(
    propertyId: string,
    reason?: string,
  ): Promise<PropertyManagementResponse> {
    try {
      const response = await axiosWithToken().delete(
        `/admin/properties/${propertyId}`,
        {
          data: { reason },
        },
      );
      return {
        success: true,
        message: "Property deleted successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        error: parseHttpError(error).message,
      };
    }
  }

  /**
   * Validate property data before editing
   */
  validatePropertyData(data: PropertyEditFormData): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!data.id) {
      errors.push("Property ID is required");
    }

    if (data.price !== undefined && data.price < 0) {
      errors.push("Price must be a positive number");
    }

    if (data.bedroom_count !== undefined && data.bedroom_count < 0) {
      errors.push("Bedroom count must be a positive number");
    }

    if (data.bathroom_count !== undefined && data.bathroom_count < 0) {
      errors.push("Bathroom count must be a positive number");
    }

    if (data.number_of_plots !== undefined && data.number_of_plots < 0) {
      errors.push("Number of plots must be a positive number");
    }

    if (data.total_package !== undefined && data.total_package < 0) {
      errors.push("Total package must be a positive number");
    }

    if (
      (data as any).email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((data as any).email as string)
    ) {
      errors.push("Invalid email format");
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

const adminPropertyManagement = new AdminPropertyManagementService();
export default adminPropertyManagement;
